import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { flushSync } from 'react-dom';
import { cn } from '../lib/utils.ts';
import { Button } from './ui/button.tsx';
import { Popover } from './ui/popover.tsx';

const MONTH_NAMES = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'] as const;
const MONTH_SHORT = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'] as const;
const MONTH_KEY = /^\d{4}-(0[1-9]|1[0-2])$/;

/** Ist `value` ein Monat `YYYY-MM` (01–12)? Für Adress-Parameter wie `?monat=`. */
export function isMonthKey(value: unknown): value is string {
  return typeof value === 'string' && MONTH_KEY.test(value);
}

/** Monat `YYYY-MM` um `delta` Monate verschieben. */
export function shiftMonthKey(month: string, delta: number): string {
  const [y, m] = month.split('-').map(Number);
  const index = y! * 12 + (m! - 1) + delta;
  return `${Math.floor(index / 12)}-${String((index % 12) + 1).padStart(2, '0')}`;
}

/** `YYYY-MM` → „Februar 2027“. */
export function monthNameOf(month: string): string {
  const [y, m] = month.split('-');
  return `${MONTH_NAMES[Number(m) - 1]} ${y}`;
}

function keyOf(year: number, monthIndex: number): string {
  return `${year}-${String(monthIndex + 1).padStart(2, '0')}`;
}

/** Monat nie vor `min` und nie nach `max` (Schlüssel `YYYY-MM` vergleichen sich als Text richtig). */
function clampMonth(month: string, min: string | undefined, max: string | undefined): string {
  if (min && month < min) return min;
  return max && month > max ? max : month;
}

/**
 * Monatswahl „‹ Februar 2027 ›“ (Zeiterfassung Story 22.4, Monatsübersicht Story 22.19). Ticket 22.28 (Vorbild Apple
 * Kalender): Klick auf die Monatsangabe öffnet ein Popover (Handy: Blatt „Monat wählen“) mit Jahr-Blättern und 4×3-Raster
 * der Monate. Tastatur: Pfeile wandern im Raster (über die Jahresgrenze), Enter wählt, Esc schließt mit Fokus auf der
 * Angabe. `max` (z. B. aktueller Monat): spätere Monate und „Nächster Monat“ gesperrt; `min` (z. B. Eintrittsmonat): frühere
 * Monate, „Voriges Jahr“ und „Voriger Monat“ entsprechend gesperrt. Ohne Grenze frei.
 * `children`: weitere Knöpfe dahinter (z. B. „Heute“).
 * `title`/`step` (Kalender-Baustein, Ticket 1.330): andere Angabe statt des Monatsnamens (Woche „22.–28. Februar 2027“) und
 * eigene Pfeile (Woche vor/zurück); die Monatswahl springt weiter zum gewählten Monat.
 */
export function MonthPicker({
  month,
  onChange,
  min,
  max,
  title,
  step,
  children,
}: {
  month: string;
  onChange: (month: string) => void;
  min?: string;
  max?: string;
  title?: string;
  step?: { prevLabel: string; nextLabel: string; onPrev: () => void; onNext: () => void };
  children?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(month);
  const gridFocus = useRef(false);
  const grid = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const prevYear = useRef<HTMLButtonElement>(null);
  const nextYear = useRef<HTMLButtonElement>(null);
  const titleId = useId();
  const atMin = min !== undefined && month <= min;
  const atMax = max !== undefined && month >= max;
  const viewYear = Number(active.slice(0, 4));
  const minYear = min ? Number(min.slice(0, 4)) : Number.NEGATIVE_INFINITY;
  const maxYear = max ? Number(max.slice(0, 4)) : Number.POSITIVE_INFINITY;

  function openChange(next: boolean) {
    if (next) {
      setActive(clampMonth(month, min, max));
      gridFocus.current = true;
    }
    setOpen(next);
  }

  // Beim Öffnen den Fokus auf den gewählten Monat – nach dem Blatt (Handy), das beim Öffnen den Fokus auf sein erstes
  // Element setzt. Beim Jahr-Blättern bleibt der Fokus auf dem Jahr-Knopf.
  useEffect(() => {
    if (!open || !gridFocus.current) return;
    gridFocus.current = false;
    const frame = requestAnimationFrame(() => grid.current?.querySelector<HTMLElement>('[tabindex="0"][data-month]')?.focus());
    return () => cancelAnimationFrame(frame);
  }, [open]);

  function choose(next: string) {
    setOpen(false);
    if (next !== month) onChange(next);
    // PC/Tablet: Fokus zurück auf die Angabe (am Handy übernimmt das Blatt beim Schließen).
    requestAnimationFrame(() => {
      if (trigger.current?.isConnected && !document.activeElement?.closest('[role="dialog"]')) trigger.current.focus();
    });
  }

  function shiftYear(by: number) {
    const next = clampMonth(shiftMonthKey(active, by * 12), min, max);
    flushSync(() => setActive(next));
    // An der Grenze wird der gedrückte Knopf gesperrt – Fokus auf den Gegen-Knopf, damit er nicht verloren geht.
    const year = Number(next.slice(0, 4));
    if (by > 0 && year >= maxYear) prevYear.current?.focus();
    if (by < 0 && year <= minYear) nextYear.current?.focus();
  }

  function onGridKey(e: KeyboardEvent<HTMLButtonElement>, key: string) {
    const by = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -4, ArrowDown: 4 }[e.key];
    if (by === undefined) return;
    e.preventDefault();
    const next = clampMonth(shiftMonthKey(key, by), min, max);
    // Sofort umsetzen und fokussieren (auch über die Jahresgrenze), damit schnell getippte Pfeile keinen Schritt verlieren.
    flushSync(() => setActive(next));
    grid.current?.querySelector<HTMLElement>(`[data-month="${next}"]`)?.focus();
  }

  return (
    <div className="flex flex-wrap items-center gap-space-2" data-testid="monatswahl">
      <Button
        variant="secondary"
        size="sm"
        aria-label={step?.prevLabel ?? 'Voriger Monat'}
        disabled={!step && atMin}
        onClick={step ? step.onPrev : () => onChange(clampMonth(shiftMonthKey(month, -1), min, max))}
      >
        <ChevronLeft size={16} strokeWidth={1.5} aria-hidden />
      </Button>
      <Popover
        open={open}
        onOpenChange={openChange}
        label="Monat wählen"
        align="end"
        fixed
        autoFocus={false}
        className="w-[18rem] p-space-3"
        trigger={(props) => (
          <button
            ref={trigger}
            type="button"
            {...props}
            aria-haspopup="dialog"
            aria-live="polite"
            data-testid="monat"
            className={cn(
              'inline-flex min-h-9 min-w-0 cursor-pointer items-center gap-space-1 rounded-full px-space-2 text-row font-medium text-ink tabular-nums transition-colors duration-150',
              'hover:bg-tile focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-ring pointer-coarse:min-h-11',
              open && 'bg-tile',
            )}
          >
            {title ?? monthNameOf(month)}
            <ChevronDown size={14} strokeWidth={1.5} aria-hidden className={cn('text-ink-3 transition-transform duration-200 motion-reduce:transition-none', open && 'rotate-180')} />
          </button>
        )}
      >
        <div data-testid="monatsraster">
          <div className="mb-space-2 flex items-center justify-between gap-space-2">
            <button
              ref={prevYear}
              type="button"
              aria-label="Voriges Jahr"
              disabled={viewYear <= minYear}
              onClick={() => shiftYear(-1)}
              className="grid size-9 cursor-pointer place-items-center rounded-full text-ink-2 hover:bg-tile hover:text-ink disabled:cursor-default disabled:opacity-40 pointer-coarse:size-11"
            >
              <ChevronLeft size={18} strokeWidth={1.5} aria-hidden />
            </button>
            <span id={titleId} aria-live="polite" data-testid="monatsraster-jahr" className="text-row font-semibold text-ink tabular-nums">
              {viewYear}
            </span>
            <button
              ref={nextYear}
              type="button"
              aria-label="Nächstes Jahr"
              disabled={viewYear >= maxYear}
              onClick={() => shiftYear(1)}
              className="grid size-9 cursor-pointer place-items-center rounded-full text-ink-2 hover:bg-tile hover:text-ink disabled:cursor-default disabled:opacity-40 pointer-coarse:size-11"
            >
              <ChevronRight size={18} strokeWidth={1.5} aria-hidden />
            </button>
          </div>
          <div ref={grid} role="grid" aria-labelledby={titleId} className="flex flex-col gap-space-1">
            {[0, 1, 2].map((row) => (
              <div key={row} role="row" className="grid grid-cols-4 gap-space-1">
                {[0, 1, 2, 3].map((col) => {
                  const index = row * 4 + col;
                  const key = keyOf(viewYear, index);
                  const selected = key === month;
                  const current = key === max;
                  const blocked = (max !== undefined && key > max) || (min !== undefined && key < min);
                  return (
                    <div key={key} role="gridcell" aria-selected={selected} className="min-w-0">
                      <button
                        type="button"
                        data-month={key}
                        tabIndex={key === active ? 0 : -1}
                        aria-current={current ? 'date' : undefined}
                        disabled={blocked}
                        onClick={() => choose(key)}
                        onKeyDown={(e) => onGridKey(e, key)}
                        onFocus={() => setActive(key)}
                        className={cn(
                          'grid h-11 w-full cursor-pointer place-items-center rounded-full text-row transition-colors duration-150 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-ring disabled:cursor-default disabled:opacity-35',
                          selected ? 'bg-brand font-semibold text-on-brand' : cn('text-ink hover:bg-tile', current && 'border border-brand font-medium'),
                        )}
                      >
                        {/* Sichtbar kurz, für Hilfsmittel voll („Januar 2027“) – Name aus dem Inhalt, damit sichtbarer Text und Name nicht auseinanderlaufen. */}
                        <span aria-hidden>{MONTH_SHORT[index]}</span>
                        <span className="sr-only">{monthNameOf(key)}</span>
                      </button>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </Popover>
      <Button
        variant="secondary"
        size="sm"
        aria-label={step?.nextLabel ?? 'Nächster Monat'}
        disabled={!step && atMax}
        onClick={step ? step.onNext : () => onChange(clampMonth(shiftMonthKey(month, 1), min, max))}
      >
        <ChevronRight size={16} strokeWidth={1.5} aria-hidden />
      </Button>
      {children}
    </div>
  );
}
